import React from "react";
import { Link } from "react-router-dom";
import { useProducts } from "../hooks/useCatalog";
import { useCart } from "../context/CartContext";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const tileBg = ["tile-rose", "tile-cream", "tile-cream", "tile-rose"];

const FeaturedProducts = () => {
  const { addItem } = useCart();
  const { products } = useProducts();
  const featured = products.slice(0, 4);

  const handleAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    if (p.stock_status === "out_of_stock") return toast.error(`${p.name} is out of stock.`);
    addItem(p);
    toast.success(`${p.name} — added to your cart.`);
  };

  return (
    <section className="py-24 md:py-28 border-t border-off-white/10 bg-charcoal-2">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// 03 — FEATURED PRODUCTS</p>
            <h2 className="font-serif-display italic text-5xl md:text-7xl leading-[0.95] text-off-white">The line-up.</h2>
          </div>
          <Link to="/shop" className="kinetic-link font-mono text-[12px] tracking-[0.25em] text-off-white/80 hover:text-off-white">
            VIEW ALL →
          </Link>
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {featured.map((p, i) => {
            const spans = [
              "col-span-12 md:col-span-6 aspect-[4/3] md:aspect-[16/12]",
              "col-span-12 md:col-span-6 aspect-[4/3] md:aspect-[16/12]",
              "col-span-6 md:col-span-5 aspect-[3/4]",
              "col-span-6 md:col-span-7 aspect-[3/4] md:aspect-[16/10]"
            ];
            return (
              <Link
                to={`/product/${p.id}`}
                key={p.id}
                className={`group relative overflow-hidden border border-off-white/10 hover:border-off-white transition-colors-only ${spans[i]} ${tileBg[i]}`}
              >
                <img src={p.image} alt={p.name} className="w-full h-full object-contain p-6 md:p-10 transition-transform duration-700 group-hover:scale-105" />

                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-off-white/70">0{i + 1}</span>
                  {p.badge && (
                    <span className="font-mono text-[10px] tracking-[0.25em] px-2 py-0.5 bg-off-white text-charcoal font-semibold">
                      {p.badge}
                    </span>
                  )}
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 flex items-end justify-between gap-4 bg-gradient-to-t from-charcoal via-charcoal/80 to-transparent pt-16">
                  <div>
                    <h3 className="font-serif-display italic text-2xl md:text-3xl text-off-white leading-tight">{p.name}</h3>
                    <p className="text-off-white/70 text-sm mt-1">{p.tagline}</p>
                    <div className="mt-3 flex items-baseline gap-3">
                      <span className="font-display text-2xl md:text-3xl text-rose">${p.price.toFixed(2)}</span>
                      {p.originalPrice && (
                        <span className="font-mono text-sm text-off-white/40 line-through">${p.originalPrice.toFixed(2)}</span>
                      )}
                      <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/50">USD · {p.size}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleAdd(e, p)}
                    className="shrink-0 w-12 h-12 border border-rose bg-rose text-charcoal hover:bg-off-white hover:border-off-white flex items-center justify-center transition-colors-only"
                    aria-label="Add to cart"
                  >
                    <Plus size={20} strokeWidth={1.5} />
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
